/**
 * Shared CSV export utilities.
 *
 * The pure helpers (`escapeCsvField`, `rowsToCsv`, `formatPercent`,
 * `buildExportFilename`) are environment-agnostic and unit-testable. The
 * browser-only helpers (`downloadCsv`, `copyCsv`) wrap the Blob/clipboard
 * APIs and must only be called from "use client" code.
 *
 * Conventions used across export call sites:
 *   - Filenames follow `{slug}-{view}[-{suffix}]-{YYYY-MM-DD}.csv` so a CSV
 *     is identifiable on a stakeholder's desktop.
 *   - `escapeCsvField` quotes any cell containing comma / quote / newline
 *     and doubles inner quotes per RFC 4180.
 *   - Percentages are rendered as integer-rounded `XX%` strings to match the
 *     UI.
 */

const NEEDS_QUOTING = /[",\n\r]/;

/**
 * Convert any cell value to its CSV-safe string form.
 * `null` / `undefined` become empty cells. Non-strings are coerced via
 * `String(value)` so callers can pass numbers and booleans directly.
 */
export function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = typeof value === "string" ? value : String(value);
  if (NEEDS_QUOTING.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from a header row and a 2D body. Each cell is run
 * through `escapeCsvField`. Lines are joined with `\n`; spreadsheet apps
 * accept `\n` and `\r\n` interchangeably and `\n` produces smaller files.
 */
export function rowsToCsv(
  headers: ReadonlyArray<string>,
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): string {
  const lines: string[] = [headers.map(escapeCsvField).join(",")];
  for (const row of rows) {
    lines.push(row.map(escapeCsvField).join(","));
  }
  return lines.join("\n");
}

/**
 * Format a 0–100 rate as `XX%` for CSV cells. Returns "" for null/undefined
 * so empty cells distinguish "not measured" from "0%".
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return `${Math.round(value)}%`;
}

/**
 * Format a 0–1 rate (e.g. citation rate) as `XX%`. Empty cell when null.
 */
export function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return `${Math.round(value * 100)}%`;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function isoDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export interface ExportFilenameOptions {
  /** Display name of the project (will be slugified). Falls back to "ripenn". */
  project?: string | null;
  /** What surface this export came from, e.g. "prompts", "competitors". */
  view: string;
  /** Optional sub-qualifier, e.g. "share-of-voice", "by-page". */
  suffix?: string | null;
  /** Defaults to "csv". */
  ext?: string;
  /** Override clock for tests. */
  date?: Date;
}

/**
 * Build a stable, human-readable export filename.
 *
 * Examples:
 *   buildExportFilename({ project: "Acme Co", view: "prompts" })
 *     → "acme-co-prompts-2026-04-27.csv"
 *   buildExportFilename({ view: "competitors", suffix: "share-of-voice" })
 *     → "ripenn-competitors-share-of-voice-2026-04-27.csv"
 */
export function buildExportFilename(opts: ExportFilenameOptions): string {
  const projectSlug = slugify(opts.project ?? "") || "ripenn";
  const viewSlug = slugify(opts.view) || "export";
  const suffixSlug = opts.suffix ? slugify(opts.suffix) : "";
  const date = isoDate(opts.date ?? new Date());
  const ext = opts.ext ?? "csv";
  const parts = [projectSlug, viewSlug, suffixSlug, date].filter(Boolean);
  return `${parts.join("-")}.${ext}`;
}

// ── Browser-only helpers ────────────────────────────────────────────────────

/**
 * Trigger a download for an arbitrary text payload. Must be called from
 * a click handler in a client component (uses Blob + URL.createObjectURL).
 */
export function downloadText(
  contents: string,
  filename: string,
  mimeType = "text/plain;charset=utf-8;",
): void {
  if (typeof window === "undefined") {
    throw new Error("downloadText must be called from the browser");
  }
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Trigger a CSV file download in the browser. Must be called from a click
 * handler in a client component.
 */
export function downloadCsv(csv: string, filename: string): void {
  downloadText(csv, filename, "text/csv;charset=utf-8;");
}

/**
 * Trigger a Markdown file download in the browser.
 */
export function downloadMarkdown(md: string, filename: string): void {
  downloadText(md, filename, "text/markdown;charset=utf-8;");
}

/**
 * Copy a CSV string to the system clipboard. Throws on permission denial
 * so call sites can surface their own toast.
 */
export async function copyCsv(csv: string): Promise<void> {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.clipboard === "undefined"
  ) {
    throw new Error("Clipboard API not available in this environment");
  }
  await navigator.clipboard.writeText(csv);
}
